import { type BotPlanningContext, type Selected, selected } from "./bot-policy-contract.js";
import { seededOrder } from "./bot-seed.js";
import type { BotMemory } from "./bot-types.js";
import { microFromString } from "./exact-numbers.js";
import type { SeatObservation, SeatState } from "./types.js";

export function personalityIntent(
  observation: SeatObservation,
  memory: BotMemory,
  context: BotPlanningContext,
): Selected | undefined {
  if (context.personality === "builder") return builder(observation, memory);
  if (context.personality === "defender") return defender(observation, memory);
  if (context.personality === "raider") return raider(observation, memory, context);
  if (context.personality === "siege-planner") return siegePlanner(observation, memory);
  return broker(observation, memory, context);
}

function builder(observation: SeatObservation, memory: BotMemory): Selected | undefined {
  const queue = observation.self.manufacturing.construction;
  if (memory.decision % 4 === 0 && queue.length < 32)
    return selected(
      {
        kind: "enqueue-queue-order",
        queue: "construction",
        target: "mining-rig",
        quantity: "5",
        repeat: false,
      },
      "Builder reinvests in Mining Rigs",
    );
  if (memory.decision % 4 === 1)
    return selected(
      {
        kind: "set-power",
        industry: 100,
        defenses: 60,
        militaryProduction: 60,
        research: 80,
      },
      "Builder favors industrial operation",
    );
  return research(observation, ["factory-automation", "factory-works", "better-drills-1"]);
}

function defender(observation: SeatObservation, memory: BotMemory): Selected | undefined {
  if (memory.decision % 3 === 0 && observation.self.manufacturing.assembly.length < 32)
    return selected(
      {
        kind: "enqueue-queue-order",
        queue: "assembly",
        target: "guard-robot",
        quantity: "5",
        repeat: false,
      },
      "Defender commissions Guard Robots",
    );
  if (memory.decision % 3 === 1)
    return selected(
      {
        kind: "set-power",
        industry: 70,
        defenses: 100,
        militaryProduction: 100,
        research: 55,
      },
      "Defender prioritizes shields and Guards",
    );
  return selected(
    { kind: "set-shields", allocations: equalAllocations(livingBorders(observation)) },
    "Defender balances border shield recharge",
  );
}

function raider(
  observation: SeatObservation,
  memory: BotMemory,
  context: BotPlanningContext,
): Selected | undefined {
  const target = weakestNeighbor(observation, context, memory);
  if (!target) return undefined;
  const phase = memory.decision % 4;
  if (phase === 0)
    return selected(
      { kind: "set-target", targetSeatId: target.id },
      "Raider selects vulnerable industry",
      target.id,
    );
  if (phase === 1)
    return selected(
      { kind: "set-infrastructure-target", borderId: target.id, target: "mining-rig" },
      "Raider targets Mining Rigs",
      target.id,
    );
  if (phase === 2)
    return selected(
      { kind: "set-standing-order", borderId: target.id, order: "raid" },
      "Raider orders a raid",
      target.id,
    );
  return deploy(observation, target.id);
}

function siegePlanner(observation: SeatObservation, memory: BotMemory): Selected | undefined {
  if (!hasResearch(observation, "siege-guns")) return research(observation, ["siege-guns"]);
  const assembly = observation.self.manufacturing.assembly;
  if (assembly.length > 0 && assembly[0]?.target !== "siege-gun")
    return selected(
      { kind: "cancel-queue-order", queue: "assembly", orderId: assembly[0]?.id ?? "" },
      "Siege Planner clears the assembly front",
    );
  if (memory.decision % 3 === 0 && assembly.length < 32)
    return selected(
      {
        kind: "enqueue-queue-order",
        queue: "assembly",
        target: "siege-gun",
        quantity: "2",
        repeat: false,
      },
      "Siege Planner commissions Siege Guns",
    );
  const target = livingBorders(observation)[0];
  if (!target) return undefined;
  if (memory.decision % 3 === 1)
    return selected(
      { kind: "set-infrastructure-target", borderId: target, target: "shield-generator" },
      "Siege Planner prepares a cleared front",
      target,
    );
  return deploy(observation, target);
}

function broker(
  observation: SeatObservation,
  memory: BotMemory,
  context: BotPlanningContext,
): Selected | undefined {
  const incoming = observation.diplomacy.pacts.find(
    (pact) =>
      pact.status === "open" &&
      pact.proposals.some((proposal) => proposal.from !== observation.self.id),
  );
  if (incoming) {
    const target = incoming.left === observation.self.id ? incoming.right : incoming.left;
    return selected(
      { kind: "accept-pact", targetSeatId: target },
      "Broker accepts a useful peace offer",
      target,
    );
  }
  const active = observation.diplomacy.pacts.find(
    (pact) => pact.status === "active" && [pact.left, pact.right].includes(observation.self.id),
  );
  if (active && microFromString(observation.self.metal) >= 500_000_000n) {
    const target = active.left === observation.self.id ? active.right : active.left;
    return selected(
      { kind: "send-metal", targetSeatId: target, amount: "100", confirmed: true },
      "Broker sends aid to a pact partner",
      target,
    );
  }
  if (observation.gameTimeMs < memory.diplomacyCooldownUntilMs) return undefined;
  const candidates = observation.seats.filter(
    (seat) => seat.alive && seat.id !== observation.self.id,
  );
  const target = seededOrder(
    candidates,
    context.rootSeed,
    observation.self.id,
    memory.seedCursor,
  )[0];
  if (!target) return undefined;
  return selected(
    {
      kind: "propose-pact",
      targetSeatId: target.id,
      message: `Offer: peace while we both defend against ${strongestEnemy(observation, target.id)}.`,
    },
    "Broker offers peace from an explicit threat estimate",
    target.id,
  );
}

function research(observation: SeatObservation, targets: readonly string[]): Selected | undefined {
  const target = targets.find((id) => !hasResearch(observation, id));
  return target
    ? selected(
        { kind: "enqueue-research", researchKind: "technology", target },
        `Queue research: ${target}`,
      )
    : undefined;
}

function hasResearch(observation: SeatObservation, target: string): boolean {
  const state = observation.self.research;
  return (
    state.completed.includes(target as never) ||
    state.active?.order.target === target ||
    state.queue.some((order) => order.target === target)
  );
}

export { botWave as deploy } from "./bot-reinforcement.js";

export function weakestNeighbor(
  observation: SeatObservation,
  context: BotPlanningContext,
  memory: BotMemory,
): SeatState | undefined {
  const borders = new Set(livingBorders(observation));
  const candidates = observation.seats
    .filter((seat) => borders.has(seat.id) && seat.alive)
    .sort(
      (left, right) => Number(left.headquartersIntegrity) - Number(right.headquartersIntegrity),
    );
  const weakest = candidates[0];
  const tied = candidates.filter(
    (seat) => seat.headquartersIntegrity === weakest?.headquartersIntegrity,
  );
  return seededOrder(tied, context.rootSeed, observation.self.id, memory.seedCursor)[0];
}

function strongestEnemy(observation: SeatObservation, excluded: string): string {
  return (
    observation.seats
      .filter((seat) => seat.alive && seat.id !== observation.self.id && seat.id !== excluded)
      .sort(
        (left, right) => Number(right.headquartersIntegrity) - Number(left.headquartersIntegrity),
      )[0]?.name ?? "the strongest remaining town"
  );
}

function livingBorders(observation: SeatObservation): string[] {
  return Object.keys(observation.self.logistics.borders)
    .filter((id) => observation.seats.find((seat) => seat.id === id)?.alive)
    .sort();
}

function equalAllocations(ids: readonly string[]): Readonly<Record<string, number>> {
  const base = ids.length ? Math.floor(100 / ids.length) : 0;
  let leftover = 100 - base * ids.length;
  return Object.fromEntries(ids.map((id) => [id, base + (leftover-- > 0 ? 1 : 0)]));
}

import { botWave as deploy } from "./bot-reinforcement.js";
